import { Button, Grid, Table, Toggle } from '@mairistem/composants';
import { formatMontant } from '@mairistem/finances-utils';
import { isNewIdentifiant } from '@mairistem/tools';
import { map } from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { ProjetStatus } from '@/src/constants/projetStatus';
import type { Projet } from '@/src/interfaces/projet';
import type { Subvention } from '@/src/interfaces/subvention';
import { getMontantRealiseConditions } from '@/src/utils/conditionVersement';
import { getMontantProjet, getPrevisisonFinancement } from '@/src/utils/projet';
import FinancementPieChart from './Chart/FinancementPieChart';
import '@/src/components/ProjetFichePanel/Financement/Financement.css';

interface FinancementProps {
	projet: Projet;
	onCreateSubvention: any;
}

const Financement = ({ projet, onCreateSubvention }: FinancementProps) => {
	const [modeHT, setModeHT] = React.useState(true);
	const history = useHistory();

	const montantProjet = getMontantProjet(projet, modeHT);

	const prevision = getPrevisisonFinancement(projet, modeHT);
	const realisation = getMontantRealiseConditions(
		projet?.subventions?.flatMap((s) => s.conditionsVersements),
		modeHT,
	);
	const realisationAutofinance = (montantProjet ?? 0) - realisation;

	const handleSubventionClick = (identifiant: number) => {
		history.push(`/subventionsPercues/${identifiant}`);
	};

	const handleChangeModeHT = (index) => {
		setModeHT(index === 0);
	};

	const getTaux = (subvention: Subvention): number => {
		if (montantProjet === 0) {
			return 0;
		}
		return (subvention.mntAttribue / montantProjet) * 100;
	};

	return (
		<>
			<div id="modeRealisationFinancement">
				<div>
					<Toggle onChange={handleChangeModeHT}>
						<Button active={modeHT} data-testid="modeHT">
							HT
						</Button>
						<Button active={!modeHT} data-testid="modeTTC">
							TTC
						</Button>
					</Toggle>
				</div>
			</div>
			<Grid>
				<Grid.Row columns={2}>
					<Grid.Column className="flex" width={10}>
						<Grid className="financement-table">
							<Grid.Row columns={2} stretched>
								<Grid.Column />
								<Grid.Column className="italic" textAlign="right">
									Prévisionnel
								</Grid.Column>
							</Grid.Row>
							<Grid.Row columns={2}>
								<Grid.Column className="bold flex">
									<div className="autofinancement legend" />
									Autofinancement
								</Grid.Column>
								<Grid.Column className="bold" textAlign="right">
									{formatMontant(prevision?.autofinancement)}
								</Grid.Column>
							</Grid.Row>
							<Grid.Row columns={2}>
								<Grid.Column className="bold flex">
									<div className="financement_externes legend" />
									Financements externes
								</Grid.Column>
								<Grid.Column className="bold" textAlign="right">
									{formatMontant(prevision?.externe)}
								</Grid.Column>
							</Grid.Row>
						</Grid>
						<FinancementPieChart
							autofinancement={prevision?.autofinancement}
							externe={prevision?.externe}
							montantProjet={montantProjet}
						/>
					</Grid.Column>

					<Grid.Column className="flex" width={6}>
						<Grid className="financement-table">
							<Grid.Row columns={2} stretched>
								<Grid.Column className="italic" textAlign="right">
									Réel
								</Grid.Column>
							</Grid.Row>
							<Grid.Row columns={2}>
								<Grid.Column className="bold" textAlign="right">
									{formatMontant(realisationAutofinance)}
								</Grid.Column>
							</Grid.Row>
							<Grid.Row columns={2}>
								<Grid.Column className="bold" textAlign="right">
									{formatMontant(realisation)}
								</Grid.Column>
							</Grid.Row>
						</Grid>

						<FinancementPieChart
							autofinancement={realisationAutofinance}
							externe={realisation}
							montantProjet={montantProjet}
						/>
					</Grid.Column>
				</Grid.Row>
			</Grid>

			<Button
				disabled={
					isNewIdentifiant(projet?.identifiant) ||
					projet?.status === ProjetStatus.CLOTURE
				}
				className="liste-table-add-button"
				basic
				content="Ajouter une subvention"
				icon="add"
				onClick={onCreateSubvention}
				data-testid="addSubventionFromProjetButton"
			/>
			<Table celled striped data-testid="financement-table">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell textAlign="left">Financeur</Table.HeaderCell>
						<Table.HeaderCell textAlign="left">
							Libellé du dossier
						</Table.HeaderCell>
						<Table.HeaderCell textAlign="left">Référence</Table.HeaderCell>
						<Table.HeaderCell textAlign="right">
							Montant attribué
						</Table.HeaderCell>
						<Table.HeaderCell textAlign="right">Taux</Table.HeaderCell>
					</Table.Row>
				</Table.Header>

				<Table.Body>
					{map(projet?.subventions, (subvention) => (
						<Table.Row
							onClick={() => handleSubventionClick(subvention?.identifiant)}
							className="pointer"
						>
							<Table.Cell textAlign="left">
								{subvention?.financeur?.nom} {subvention?.financeur?.prenom}
							</Table.Cell>
							<Table.Cell textAlign="left">{subvention?.libelle}</Table.Cell>
							<Table.Cell textAlign="left">
								{subvention?.referenceDossier}
							</Table.Cell>
							<Table.Cell textAlign="right">
								{formatMontant(subvention?.mntAttribue)}
							</Table.Cell>
							<Table.Cell textAlign="right">
								{formatMontant(getTaux(subvention), 2, null, null, true)}
							</Table.Cell>
						</Table.Row>
					))}

					{(projet?.subventions?.length ?? 0) === 0 && (
						<Table.Row>
							<Table.Cell textAlign="center" colSpan={5}>
								Aucune donnée à afficher
							</Table.Cell>
						</Table.Row>
					)}
				</Table.Body>
			</Table>
		</>
	);
};

export default Financement;
